import { useAuth0 } from "@auth0/auth0-react";
import { useSessionStorage } from "@mantine/hooks";
import { Stack, Typography } from "@mui/material";
import { noop } from "lodash-es";
import React, { useEffect, useMemo } from "react";

import { CustomerSelector } from "../../components/customer/CustomerSelector";
import { LogoutButton } from "../../components/layout/LogoutButton";
import { NoAuthLayout } from "../../components/layout/page/NoAuthLayout";
import { OmiMinimalLayout } from "../../omi/ui/layout/OmiMinimalLayout";
import { ACTIVE_APPLICATION_STATUSES, isCustomerActive } from "../../utils/customerUtils";
import { SELECTED_CUSTOMER_ID } from "../../utils/sessionStorage";
import {
    ICustomerContextApplicationSummaryFragment,
    ICustomerContextCustomerFragment,
} from "./CustomerContext.generated";

export interface ICustomerContext {
    applications: ICustomerContextApplicationSummaryFragment[] | undefined;
    customers: ICustomerContextCustomerFragment[] | undefined;
    selectedCustomer: ICustomerContextCustomerFragment | undefined;
    setSelectedCustomerId(id: string): void;
}

export const CustomerContext = React.createContext<ICustomerContext>({
    applications: undefined,
    customers: undefined,
    selectedCustomer: undefined,
    setSelectedCustomerId: noop,
});

interface IProps {
    applications: ICustomerContextApplicationSummaryFragment[] | undefined;
    customers: ICustomerContextCustomerFragment[] | undefined;
    isOmi?: boolean;
}

export const CustomerContextProvider: React.FC<React.PropsWithChildren<IProps>> = ({
    applications,
    customers,
    children,
    isOmi = false,
}) => {
    // `getCurrentUser` returns null until a customer is selected, so rely on Auth0
    const { user } = useAuth0();

    // Only set a default organization if there is only one active organization and no active applications
    const activeCustomers = (customers ?? []).filter((c) => isCustomerActive(c));
    const activeApplications = (applications ?? []).filter((a) => ACTIVE_APPLICATION_STATUSES.includes(a.status));
    const defaultSelectedCustomerId =
        activeCustomers.length === 1 && activeApplications.length === 0 ? activeCustomers[0].id : undefined;

    const [selectedCustomerId, setSelectedCustomerId] = useSessionStorage<string | undefined>({
        key: SELECTED_CUSTOMER_ID,
        defaultValue: defaultSelectedCustomerId,
        // TODO(@lucaslemanowicz): check to make sure this works in multi-org setting, with two active orgs
        getInitialValueInEffect: false, // Prevents a flicker of the customer selector when the user is logged in
    });

    // We require this because useSessionStorage doesn't actually save the defaultValue to storage, so it causes
    // issues for customers that have a default org they should be a part of (because the headers are not properly
    // set). This workaround is not ideal, but it will work for now until the Mantine hooks are fixed.
    useEffect(() => {
        if (sessionStorage.getItem(SELECTED_CUSTOMER_ID) == null && selectedCustomerId != null) {
            setSelectedCustomerId(selectedCustomerId);
        }
    }, [selectedCustomerId, setSelectedCustomerId]);

    const contextValue = useMemo((): ICustomerContext => {
        const selectedCustomer = (customers ?? []).find((c) => c.id === selectedCustomerId);
        return { applications, customers, selectedCustomer, setSelectedCustomerId };
    }, [applications, customers, selectedCustomerId, setSelectedCustomerId]);

    // Multi-tenant users must select an org.
    const loggedInUserHasNotSelectedOrg = user != null && contextValue.selectedCustomer == null;
    // But don't let that stop new users (no org) from accessing the Omi signup page.
    const isOmiUserWithoutMultipleOrgs = isOmi && (customers?.length ?? 0) < 2;
    if (loggedInUserHasNotSelectedOrg && !isOmiUserWithoutMultipleOrgs) {
        const customerSelector = (
            <Stack alignItems="center" spacing={4}>
                <Stack spacing={2} alignItems="center">
                    <Typography variant="h1">Select Organization</Typography>
                    {user.email && <Typography>Logged in as {user.email}</Typography>}
                </Stack>
                <CustomerSelector
                    customers={customers ?? []}
                    applications={applications ?? []}
                    onCustomerSelect={setSelectedCustomerId}
                    isOmi={isOmi}
                />
            </Stack>
        );
        return isOmi ? (
            <OmiMinimalLayout showHeaderLogo={true} actions={<LogoutButton />}>
                {customerSelector}
            </OmiMinimalLayout>
        ) : (
            <NoAuthLayout title="Levro" actions={<LogoutButton />}>
                {customerSelector}
            </NoAuthLayout>
        );
    }

    return <CustomerContext.Provider value={contextValue}>{children}</CustomerContext.Provider>;
};
